<template>
  <section
    v-if="isMounted && !loading"
    :id="anchor"
    :class="sectionClass"
    class="block block-cards"
  >
    <div class="container">
      <PageHierarchySection
        v-if="allStories.length"
        v-editable="blok || {}"
        :filtered-audiences="filteredAudiencesComputed"
        :filtered-topics="filteredTopicsComputed"
        :section="contentType"
        :subheading="subheading"
        :title="title"
      >
        <SliderComponent
          v-if="isSlider"
          :content-type="contentType"
          :cta-title="title"
          :items="allStories"
          class="slider--cards"
          total-slides="6"
        />
        <div v-else class="block-card-list">
          <component
            :is="listType"
            :blok="allStories"
            :content-type="contentType"
            :is-filtered="allStories.length < maxQuantity"
          />
        </div>
      </PageHierarchySection>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { QueryParams } from '~/modules/queryFormatters/DefaultQueryFormatter'
import { QueryFormatterFactory } from '~/modules/queryFormatters/QueryFormatterFactory'
import type { BlockCardsStoryblok } from '~/types/storyblok'

export interface CardProps {
  name: string
  media: {
    filename: string
  }
  full_slug: string
  content: {
    audiences: string[]
    topics: string[]
    description: string
  }
}

const props = withDefaults(
  defineProps<{
    blok?: BlockCardsStoryblok
    totalStories?: number
    filteredTopics?: string[]
    filteredAudiences?: string[]
    blockTitle?: string
  }>(),
  {
    totalStories: 4,
    filteredTopics: () => [],
    filteredAudiences: () => [],
    blockTitle: '',
    blok: () => undefined,
  },
)
const { pageId, sectionClass, pageComponentType } = useStoryContent(
  toRefs(props),
)
const { fetchAsyncData } = useStoryblokHelpers()
const { fetchAudiences, fetchTopics } = useStoryblokDataSources()
const { data: datasourceAudiences } = await fetchAudiences()
const { data: datasourceTopics } = await fetchTopics()
const { viewedStories } = storeToRefs(useViewedStoriesStore())
const availableSliders = ['news', 'events', 'real-stories', 'featured-topics']

// Check component is mounted and check both fetch requests are done
const loading = computed(() =>
  [selectedStoriesStatus.value, additionalStoriesStatus.value].includes(
    'pending',
  ),
)

const isMounted = ref(false)
onMounted(() => {
  isMounted.value = true
})

const contentType = computed(() => props.blok?.content_type)

const title = computed(() => props.blok?.title || props.blockTitle)
const subheading = computed(() => props.blok?.subheading || '')
const listType = computed(() => contentType.value + 'List')
const maxQuantity = computed(
  () => parseInt(props.blok?.quantity) || props.totalStories,
)
const defaultTopics = computed(() =>
  datasourceTopics.value.map((topic) => topic.value),
)
const defaultAudiences = computed(() =>
  datasourceAudiences.value.map((audience) => audience.value),
)
const filteredTopicsComputed = computed(() =>
  props.blok?.topics?.length
    ? props.blok.topics.map((topic) => topic.toString())
    : props.filteredTopics || defaultTopics.value,
)
const queryFormatter = computed(() =>
  QueryFormatterFactory(slugToComponentTypeName(contentType.value)),
)

const filteredAudiencesComputed = computed(() =>
  props.blok?.audiences?.length
    ? props.blok.audiences.map((audience) => audience.toString())
    : props.filteredAudiences || defaultAudiences.value,
)

const sortBy = computed(() =>
  contentType.value === 'resources' || contentType.value === 'service-providers'
    ? 'published_at:desc'
    : queryFormatter.value.getDefaultSort(),
)

const isSlider = computed(
  () => props.blok?.slider_mode && availableSliders.includes(contentType.value),
)

const anchor = computed(() => props.blok?.anchor)

/**
 * Fetch selected stories
 */

/*
 * ⚠️ WARNING: MUST only fetch when storyIds are set!
 * Otherwise it will fetch _all_ stories, with no filtering,
 * overriding all the other fallback data with unrelated cards,
 * which is not what we want!
 */
const storyIds = computed(() => props.blok?.stories?.join(',') ?? '')

const selectedStoriesOptions = computed(() => ({
  by_uuids_ordered: storyIds.value,
  per_page: maxQuantity.value,
}))

const {
  clear: selectedStoriesClear,
  data: selectedStoriesData,
  refresh: selectedStoriesRefresh,
  status: selectedStoriesStatus,
} = await fetchAsyncData<unknown>('stories', selectedStoriesOptions, {
  // Only fetch when storyIds are set
  immediate: storyIds.value.length > 0,
})

const selectedStories = computed(
  () => selectedStoriesData.value?.data.stories || [],
)

watch(
  () => props.blok?.stories,
  (val) => {
    if (val?.length > 0) {
      // Only refresh when storyIds are set
      selectedStoriesRefresh()
    } else {
      // Clear when storyIds are removed
      selectedStoriesClear()
    }
  },
)

/**
 * Fetch additional stories
 * Padding stories when there are less selected stories than maxQuantity
 */

const storiesRequired = computed(
  () => maxQuantity.value - selectedStories.value.length,
)

// contentTypes where we wish to showOnlyStartPages
const showOnlyStartPages = computed(() =>
  ['featured-topics', 'audiences'].includes(contentType.value),
)

const additionalStoriesOptions = computed(() => ({
  is_startpage: showOnlyStartPages.value,
  by_slugs: getAdditionalStoriesSlugs(),
  per_page: storiesRequired.value,
  filter_query: getAdditionalStoriesFilterQuery(),
  sort_by: sortBy.value,
  level: showOnlyStartPages.value ? 2 : null,
  excluding_ids: pageId.value || null,
}))

const {
  clear: additionalStoriesClear,
  data: additionalStoriesData,
  refresh: additionalStoriesRefresh,
  status: additionalStoriesStatus,
} = await fetchAsyncData<unknown>('stories', additionalStoriesOptions, {
  immediate: false,
})

const additionalStories = computed(
  () => additionalStoriesData.value?.data.stories || [],
)

watch(additionalStoriesOptions, () => {
  if (storiesRequired.value > 0) {
    additionalStoriesRefresh()
  } else {
    additionalStoriesClear()
  }
})

function getAdditionalStoriesFilterQuery() {
  // get a query designed for this type
  const options: QueryParams = {
    topics: filteredTopicsComputed.value,
  }
  if (
    pageComponentType.value === 'AudiencePage' ||
    contentType.value === 'audiences'
  ) {
    options.audiences = filteredAudiencesComputed.value
    options.ignoreAllOption = true
  }

  let filterQuery =
    queryFormatter.value.getFilterQuery(options)?.filter_query || {}

  // filter out any items we already have in the selected group
  // or have viewed recently in the session
  if (selectedStories.value.length || viewedStories.value.length) {
    filterQuery._uid = {
      not_in: [
        ...new Set(
          [...selectedStories.value, ...viewedStories.value].map(
            (item) => item.content._uid,
          ),
        ),
      ].join(','),
    }
  }
  if (!Object.keys(filterQuery).length) {
    filterQuery = null
  }
  return filterQuery
}

function getAdditionalStoriesSlugs() {
  return contentType.value + '/*'
}

/**
 * Build datasource stories - only used when no specific stories are selected
 */

const datasourceStories = computed(() => {
  if (
    // don't load datasource stories when specifics selected
    selectedStories.value.length ||
    // don't return data when CMS is already filtering
    filteredTopicsComputed.value.length ||
    filteredAudiencesComputed.value.length
  ) {
    return []
  }

  // Set datasource to the correct data source based on the contentType
  let datasource = []
  if (contentType.value === 'featured-topics') {
    datasource = [...datasourceTopics.value]
  } else if (contentType.value === 'audiences') {
    datasource = [...datasourceAudiences.value]
  }

  if (!datasource.length) {
    return datasource
  }

  // remove "all" from data
  datasource = datasource.filter((item) => item.value !== 'all')

  // map slugs of selected or additional items
  const selectedSlugs = selectedStories.value
    .map((story) => story.slug)
    .concat(additionalStories.value.map((story) => story.slug))

  // Use slugs to filter out any items found in selected Storyblok data
  datasource = datasource.filter((item) => {
    return !selectedSlugs.includes(item.value)
  })

  // set up data for Cards
  return datasource.map(
    (item): CardProps => ({
      name: item.name,
      media: {
        filename: item.media,
      },
      full_slug: contentType.value + '/' + item.value,
      content: {
        audiences: [item.value],
        topics: [item.value],
        description:
          'Discover a range of information and resources for ' +
          item.name +
          ' audiences',
      },
    }),
  )
})

/**
 * Combine all stories in correct order
 */
const allStories = computed(() => [
  ...selectedStories.value,
  ...additionalStories.value,
  ...datasourceStories.value,
])
</script>

<style lang="less" scoped>
.block-card-list {
  margin: 0 -@gutter;
  @media @tablet {
    margin: 0;
  }
}
</style>
